import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Users, Plane, TrendingUp, Award } from 'lucide-react';

// --------- HELPERS ----------
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function computeSafeTooltipPosition({ barRect, containerRect, tipW, tipH, prefAbove = true }) {
  const PADDING = 8;
  const OFFSET = 10;

  // posição relativa ao container (tooltip é absolute dentro dele)
  let x = (barRect.left - containerRect.left) + (barRect.width / 2); // centro da barra

  const yTop = (barRect.top - containerRect.top) - OFFSET - tipH;  // acima
  const yBottom = (barRect.bottom - containerRect.top) + OFFSET;   // abaixo

  let placeAbove = prefAbove;
  let y = placeAbove ? yTop : yBottom;

  // se acima não cabe, tenta abaixo
  if (placeAbove && y < PADDING) {
    placeAbove = false;
    y = yBottom;
  }
  const containerH = containerRect.height;
  // se abaixo não cabe, volta acima
  if (!placeAbove && (y + tipH + PADDING > containerH)) {
    placeAbove = true;
    y = yTop;
  }

  // clamp final
  y = clamp(y, PADDING, Math.max(PADDING, containerH - tipH - PADDING));
  // para X, como vamos usar translateX(-50%), o clamp precisa considerar meia-largura
  const minCenter = PADDING + tipW / 2;
  const maxCenter = containerRect.width - PADDING - tipW / 2;
  x = clamp(x, minCenter, maxCenter);

  return { x, y, placeAbove };
}
// ----------------------------

export default function PassengerChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBarKey, setHoveredBarKey] = useState(null);
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const innerRefDesktop = useRef(null);
  const innerRefMobile = useRef(null);
  const tooltipRef = useRef(null);

  const passengerData = [
    { year: 2010, domestic: 70.0, international: 15.4 }, { year: 2011, domestic: 81.9, international: 17.9 },
    { year: 2012, domestic: 88.5, international: 18.9 }, { year: 2013, domestic: 90.0, international: 19.8 },
    { year: 2014, domestic: 95.8, international: 21.3 }, { year: 2015, domestic: 96.1, international: 21.5 },
    { year: 2016, domestic: 88.6, international: 20.9 }, { year: 2017, domestic: 90.6, international: 21.9 },
    { year: 2018, domestic: 93.6, international: 24.1 }, { year: 2019, domestic: 95.0, international: 24.2 },
    { year: 2020, domestic: 45.2, international: 6.8 }, { year: 2021, domestic: 62.6, international: 4.8 },
    { year: 2022, domestic: 82.2, international: 15.7 }, { year: 2023, domestic: 91.4, international: 21.5 },
    { year: 2024, domestic: 93.4, international: 25.5 }
  ];

  const CHART_HEIGHT = 320;
  const maxValue = Math.max(...passengerData.map(d => Math.max(d.domestic, d.international)));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setAnimatedData(passengerData), 400);
    return () => clearTimeout(t);
  }, []);

  const getBarHeight = (v) => (v / maxValue) * (CHART_HEIGHT - 40);

  // Mostrar tooltip (desktop e mobile)
const showTooltip = (item, key, targetEl) => {
  const container = (isMobile ? innerRefMobile : innerRefDesktop).current;
  if (!container) return;

  const barRect = targetEl.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  setHoveredBarKey(key);

  const isAuto = key.endsWith('auto');
  const value  = isAuto ? item.auto : item.commercial;
  const label  = isAuto ? 'Automóveis' : 'Comerciais Leves';

  // 1) Primeiro renderiza o tooltip
  setTooltipData({ year: item.year, value, label });

  // 2) No próximo frame, mede e posiciona
  requestAnimationFrame(() => {
    const tipEl = tooltipRef.current;
    const tipW  = tipEl ? tipEl.offsetWidth  : 140;
    const tipH  = tipEl ? tipEl.offsetHeight : 44;

    const { x, y, placeAbove } = computeSafeTooltipPosition({
      barRect,
      containerRect,
      tipW,
      tipH,
      prefAbove: !isMobile, // no mobile, preferir abaixo
    });

    setTooltipPos({ x, y, placeAbove });
  });
};


  const handleEnter = (item, key) => (e) => {
    showTooltip(item, key, e.currentTarget);
  };

  const handleLeave = () => {
    setHoveredBarKey(null);
    setTooltipData(null);
    setTooltipPos(null);
  };

  // Escolhe os handlers certos por plataforma
  const getBarHandlers = (item, key) => {
    if (isMobile) {
      return {
  onTouchStart: (e) => {
  e.preventDefault();              // evita zoom/selection
  showTooltip(item, key, e.currentTarget);
},
        onTouchEnd: handleLeave, // opcional: esconder ao soltar
      };
    }
    // Desktop: redundância (pointer + mouse) e atualização contínua
    return {
      onPointerEnter: (e) => handleEnter(item, key)(e),
      onPointerMove:  (e) => handleEnter(item, key)(e),
      onPointerLeave: handleLeave,
      onMouseEnter:   (e) => handleEnter(item, key)(e),
      onMouseMove:    (e) => handleEnter(item, key)(e),
      onMouseLeave:   handleLeave,
    };
  };

  // --- mobile ---
  const BAR_W_MOBILE = 16;
  const GAP_BETWEEN_BARS = 6;
  const GAP_BETWEEN_GROUPS = 14;
  const GROUP_W_MOBILE = (BAR_W_MOBILE * 2) + GAP_BETWEEN_BARS + GAP_BETWEEN_GROUPS;
  const contentWidthMobile = (passengerData.length * GROUP_W_MOBILE) + 16;

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Evolução do Transporte de Passageiros</CardTitle>
          <p className="text-center text-gray-600">
            Passageiros transportados nos mercados doméstico e internacional (em milhões)
          </p>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            <div className="overflow-x-auto no-scrollbar" onScroll={handleLeave}>
              <div
                className="relative flex items-end px-4"
                ref={innerRefMobile}
                style={{ height: CHART_HEIGHT, width: contentWidthMobile, paddingTop: 6  }}
              >
                {passengerData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end" style={{ width: GROUP_W_MOBILE }}>
                    <div className="flex items-end" style={{ gap: GAP_BETWEEN_BARS }}>
                      {/* Doméstico */}
                      <div
                        className={`rounded-t cursor-pointer ${hoveredBarKey === `${index}-domestic` ? "bg-sky-600" : "bg-sky-500"}`}
                        style={{
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.domestic) : 0}px`,
                          transitionDelay: `${index * 40}ms`,
                        }}
                        {...getBarHandlers(item, `${index}-domestic`)}
                      />
                      {/* Internacional */}
                      <div
                        className={`rounded-t cursor-pointer ${hoveredBarKey === `${index}-international` ? "bg-emerald-600" : "bg-emerald-500"}`}
                        style={{
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.international) : 0}px`,
                          transitionDelay: `${index * 40 + 16}ms`,
                        }}
                        {...getBarHandlers(item, `${index}-international`)}
                      />
                    </div>
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}
                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                    style={{
                      left: tooltipPos ? tooltipPos.x : -9999,
                      top: tooltipPos ? tooltipPos.y : -9999,
                      transform: 'translate(-50%, -10%)'
                    }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">{tooltipData.label}: {tooltipData.value.toFixed(1)} M</div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-2">
                {passengerData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end h-full">
                    <div className="flex items-end gap-1 h-full">
                      {/* Doméstico */}
                      <div
                        className={`w-4 rounded-t cursor-pointer ${hoveredBarKey === `${index}-domestic` ? "bg-sky-600" : "bg-sky-500"}`}
                        style={{ height: `${animatedData[index] ? getBarHeight(item.domestic) : 0}px` }}
                        {...getBarHandlers(item, `${index}-domestic`)}
                      />
                      {/* Internacional */}
                      <div
                        className={`w-4 rounded-t cursor-pointer ${hoveredBarKey === `${index}-international` ? "bg-emerald-600" : "bg-emerald-500"}`}
                        style={{ height: `${animatedData[index] ? getBarHeight(item.international) : 0}px` }}
                        {...getBarHandlers(item, `${index}-international`)}
                      />
                    </div>
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>
              {tooltipData && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                  style={{
                    left: tooltipPos ? tooltipPos.x : -9999,
                    top: tooltipPos ? tooltipPos.y : -9999,
                    transform: 'translate(-50%, -10%)'
                  }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">{tooltipData.label}: {tooltipData.value.toFixed(1)} M</div>
                </div>
              )}
            </div>
          )}
          <div className="mt-6 flex justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-sky-500 rounded"></div>
              <Users className="w-4 h-4 mr-1" />
              <span>Passageiros Domésticos</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-500 rounded"></div>
              <Plane className="w-4 h-4 mr-1" />
              <span>Passageiros Internacionais</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* --- Cards Analíticos --- */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-emerald-50 to-green-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-emerald-900 flex items-center gap-2">
              <Award className="w-6 h-6" />
              Recorde Histórico Internacional
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-emerald-800 mb-2">Superação dos Níveis Pré-Pandemia</h4>
              <p className="text-sm text-gray-700">
                O mercado internacional bateu recorde histórico em 2024 com 25,5 milhões de passageiros, 
                superando o anterior de 2019 (24,2 milhões) e crescendo 18,9% em relação a 2023.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-emerald-800 mb-2">Fevereiro Recordista</h4>
              <p className="text-sm text-gray-700">
                A ANAC registrou 2,04 milhões de passageiros em voos internacionais em fevereiro de 2024, 
                o maior volume já observado para este mês na série histórica.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-sky-50 to-blue-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-sky-900 flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              Recuperação do Mercado Doméstico
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-sky-800 mb-2">Retomada Gradual</h4>
              <p className="text-sm text-gray-700">
                O mercado doméstico transportou 93,4 milhões de passageiros em 2024, crescimento de 2,1% 
                em relação a 2023, aproximando-se do recorde de 2019 (95 milhões).
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-sky-800 mb-2">Impacto da Pandemia</h4>
              <p className="text-sm text-gray-700">
                Após a queda drástica para 45,2 milhões em 2020, o setor doméstico demonstrou 
                recuperação consistente, com crescimento sustentado desde 2021.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-gradient-to-r from-blue-50 to-sky-50">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-blue-900">
            Segundo Melhor Ano da História da Aviação Brasileira
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/60 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">118,9 M</div>
              <h4 className="font-semibold text-blue-800 mb-2">Total de Passageiros</h4>
              <p className="text-sm text-gray-700">Soma dos mercados doméstico e internacional em 2024</p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">4%</div>
              <h4 className="font-semibold text-green-800 mb-2">Crescimento Total</h4>
              <p className="text-sm text-gray-700">Crescimento geral em relação a 2023, com acréscimo de 5,9 milhões</p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">2019</div>
              <h4 className="font-semibold text-purple-800 mb-2">Único Ano Superior</h4>
              <p className="text-sm text-gray-700">Apenas 2019 registrou mais passageiros (119,1 milhões) na história</p>
            </div>
          </div>
          <div className="bg-white/60 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">Perspectivas Futuras</h4>
            <p className="text-sm text-gray-700">
              A expectativa é de crescimento contínuo do transporte aéreo de passageiros, alinhado com a tendência 
              global de expansão das viagens aéreas. O crescimento da aviação está diretamente relacionado à geração 
              de empregos, desenvolvimento de tecnologias e fortalecimento de mercados em todas as economias, 
              segundo a Associação Internacional de Transporte Aéreo (IATA).
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
